// ASO.Market: motivated ratings, reviews and installs for our Play apps,
// ordered through their API. Campaigns sync every 10 minutes (no webhooks on
// their side); status changes on our apps reach the release topic in Telegram.
// ?app=<id> opens a new order for that app, ?campaign=<id> opens a campaign.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { getOverview, listCampaigns, syncCampaigns, type AsomCampaign } from '@/api/asoMarket';
import { OrderModal } from './aso-market/OrderModal';
import { CampaignModal } from './aso-market/CampaignModal';
import { MoneyTab } from './aso-market/MoneyTab';
import { AccountPanel } from './aso-market/AccountPanel';
import { AutoTab } from './aso-market/AutoTab';
import {
  AppLogo, Card, Flags, Notice, Progress, Segmented, StatusPill, TypeBadge, ago, fmtDate, orderWords, usd,
} from './aso-market/ui';

type Tab = 'campaigns' | 'auto' | 'money' | 'account';
type StatusFilter = '' | 'open' | 'finished' | 'cancelled' | 'problems';
type Origin = '' | 'panel' | 'auto' | 'mine';

export function AsoMarket() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const canEdit = useCanEdit();
  const overview = useQuery({ queryKey: ['aso-market', 'overview'], queryFn: getOverview, staleTime: 60_000 });
  const ov = overview.data;
  const tab: Tab = (['campaigns', 'auto', 'money', 'account'] as const).find((t) => t === params.get('tab')) ?? 'campaigns';
  const setParam = (k: string, v: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  const [status, setStatus] = useState<StatusFilter>('');
  const [origin, setOrigin] = useState<Origin>('');
  const [q, setQ] = useState('');
  const campaigns = useQuery({
    queryKey: ['aso-market', 'campaigns', status, origin, q],
    queryFn: () => listCampaigns({ status, origin, q }),
    enabled: !!ov?.connected,
    refetchInterval: 60_000,
  });
  const all = useQuery({
    queryKey: ['aso-market', 'campaigns', 'open-all'],
    queryFn: () => listCampaigns({ status: 'open' }),
    enabled: !!ov?.connected,
    refetchInterval: 60_000,
  });

  const sync = useMutation({
    mutationFn: syncCampaigns,
    onSuccess: (r) => { toast(`Synced ${r.seen} campaigns from ASO.Market`); qc.invalidateQueries({ queryKey: ['aso-market'] }); },
    onError: (e: Error) => toast(e.message),
  });

  const orderOpen = params.has('app') || params.get('new') === '1';
  const campaignId = params.get('campaign') ? Number(params.get('campaign')) : null;

  const stats = useMemo(() => {
    const list = all.data?.campaigns ?? [];
    return {
      running: list.filter((c) => c.status === 20).length,
      awaiting: list.filter((c) => c.status === 10).length,
      inWork: list.reduce((s, c) => s + c.in_progress + c.in_validation, 0),
      problems: list.filter((c) => c.state === 'unknown' || c.state === 'submitting').length,
    };
  }, [all.data]);

  if (overview.isLoading) return <p className="text-sm text-content-subtle">Loading ASO.Market…</p>;
  if (overview.isError || !ov) return <Notice tone="danger">{(overview.error as Error)?.message ?? 'Failed to load'}</Notice>;

  const notSetUp = !ov.account.email || !ov.account.has_password;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">ASO.Market</h1>
            {!notSetUp && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ov.account.env === 'prod' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
                {ov.account.env === 'prod' ? 'Production' : 'Dev stand'}
              </span>
            )}
          </div>
          <p className="text-sm text-content-subtle mt-1">Motivated ratings, reviews and installs for our Google Play apps.</p>
        </div>
        {ov.connected && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-content-subtle hidden sm:inline">Synced {ago(campaigns.data?.synced_at)}</span>
            <Button variant="secondary" size="sm" loading={sync.isPending} onClick={() => sync.mutate()}>Sync</Button>
            {canEdit && <Button size="sm" onClick={() => setParam('new', '1')}>+ New order</Button>}
          </div>
        )}
      </div>

      {notSetUp ? (
        ov.can_manage ? (
          <div className="space-y-3">
            <Notice tone="info">Connect the ASO.Market account the panel orders with.</Notice>
            <AccountPanel overview={ov} />
          </div>
        ) : <Notice tone="info">ASO.Market is not connected yet. Ask a super admin to set up the account.</Notice>
      ) : (
        <>
          {ov.error && <Notice tone="danger">ASO.Market: {ov.error}</Notice>}
          {ov.profile?.is_banned && <Notice tone="danger">The ASO.Market account is banned on their side.</Notice>}

          {ov.connected && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Balance" value={usd(ov.finances?.balance ?? ov.profile?.balance)} action={<button type="button" className="text-xs text-primary hover:underline" onClick={() => setParam('tab', 'money')}>Top up</button>} />
              <Kpi label="Spent this month" value={usd(ov.finances?.spent_month ?? 0)} />
              <Kpi label="Running" value={String(stats.running)} sub={`${Math.round(stats.inWork)} tasks with performers`} />
              <Kpi label="Awaiting payment" value={String(stats.awaiting)} tone={stats.awaiting ? 'warning' : undefined} sub={stats.problems ? `${stats.problems} order(s) not confirmed yet` : undefined} />
            </div>
          )}

          <div className="flex gap-1 border-b border-border" role="tablist">
            {([['campaigns', 'Campaigns'], ['auto', 'Auto'], ['money', 'Money'], ...(ov.can_manage ? [['account', 'Account']] : [])] as [Tab, string][]).map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setParam('tab', id === 'campaigns' ? null : id)}
                className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === id ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'campaigns' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <input type="search" placeholder="App or package…" value={q} onChange={(e) => setQ(e.target.value)}
                  className="w-56 px-3 py-1.5 border border-border-strong rounded-md text-sm bg-surface text-content placeholder-content-subtle" />
                <Segmented value={status} onChange={setStatus} options={[
                  { value: '', label: 'All' }, { value: 'open', label: 'Open' }, { value: 'finished', label: 'Finished' },
                  { value: 'cancelled', label: 'Cancelled' }, { value: 'problems', label: 'Problems' },
                ]} />
                <Segmented value={origin} onChange={setOrigin} options={[
                  { value: '', label: 'Everyone' }, { value: 'panel', label: 'From panel' }, { value: 'auto', label: 'Auto' }, { value: 'mine', label: 'Mine' },
                ]} />
              </div>
              <CampaignTable rows={campaigns.data?.campaigns} loading={campaigns.isLoading} error={campaigns.error as Error | null} onOpen={(id) => setParam('campaign', String(id))} />
            </div>
          )}
          {tab === 'auto' && <AutoTab overview={ov} canManage={ov.can_manage} onOpenCampaigns={() => { setOrigin('auto'); setStatus(''); setParam('tab', null); }} />}
          {tab === 'money' && ov.connected && <MoneyTab overview={ov} onOpenCampaign={(id) => setParam('campaign', String(id))} />}
          {tab === 'account' && ov.can_manage && <AccountPanel overview={ov} />}
        </>
      )}

      {ov.connected && orderOpen && (
        <OrderModal open={orderOpen} overview={ov} initialAppId={params.get('app')} onClose={() => setParams((p) => { const n = new URLSearchParams(p); n.delete('app'); n.delete('new'); return n; }, { replace: true })} />
      )}
      <CampaignModal id={campaignId} onClose={() => setParam('campaign', null)} />
    </div>
  );
}

function Kpi({ label, value, sub, action, tone }: { label: string; value: string; sub?: string; action?: React.ReactNode; tone?: 'warning' }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-content-subtle">{label}</span>
        {action}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone === 'warning' && value !== '0' ? 'text-warning' : 'text-content'}`}>{value}</div>
      {sub && <div className="text-xs text-content-subtle mt-0.5">{sub}</div>}
    </Card>
  );
}

function CampaignTable({ rows, loading, error, onOpen }: { rows?: AsomCampaign[]; loading: boolean; error: Error | null; onOpen: (id: number) => void }) {
  if (loading) return <p className="text-sm text-content-subtle">Loading campaigns…</p>;
  if (error) return <Notice tone="danger">{error.message}</Notice>;
  if (!rows?.length) {
    return (
      <Card className="p-10 text-center">
        <div className="text-3xl">★</div>
        <p className="mt-2 text-sm text-content-muted">No campaigns here yet.</p>
        <p className="text-xs text-content-subtle">Place an order, or press Sync to pull campaigns made in the ASO.Market cabinet.</p>
      </Card>
    );
  }
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-content-subtle border-b border-border">
            <th className="px-4 py-2.5 font-normal">App</th>
            <th className="px-3 py-2.5 font-normal">Order</th>
            <th className="px-3 py-2.5 font-normal">Progress</th>
            <th className="px-3 py-2.5 font-normal text-right">Cost</th>
            <th className="px-3 py-2.5 font-normal">Status</th>
            <th className="px-4 py-2.5 font-normal">Ordered</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((c) => {
            const price = c.price ?? c.quoted_price;
            const how = orderWords(c).how;
            return (
              <tr key={c.id} onClick={() => onOpen(c.id)} className="cursor-pointer hover:bg-surface-muted/60">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <AppLogo src={c.app_logo_url} name={c.app_name || c.package_name} />
                    <div className="min-w-0">
                      <div className="font-medium text-content truncate max-w-[220px]">
                        {c.app_name || c.package_name}
                        {c.app_banned ? <span className="ml-1.5 text-xs text-danger font-normal">banned</span> : null}
                      </div>
                      <div className="text-xs text-content-subtle font-mono truncate max-w-[220px]">{c.package_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-col gap-1">
                    <span><TypeBadge campaign={c} />{how && <span className="ml-1.5 text-xs text-content-muted">{how}</span>}</span>
                    <Flags codes={c.countries} max={4} />
                  </div>
                </td>
                <td className="px-3 py-3"><Progress c={c} /></td>
                <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">
                  {price != null ? usd(price * c.total) : '—'}
                  {price != null && <div className="text-xs text-content-subtle">{usd(price, price < 1 ? 3 : 2)} each</div>}
                </td>
                <td className="px-3 py-3"><StatusPill campaign={c} /></td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-content-muted">{fmtDate(c.remote_created_at ?? c.created_at)}</div>
                  <div className="text-xs text-content-subtle">{c.auto ? 'auto, release plan' : c.created_by ? (c.created_by_name || c.created_by_email || 'panel') : 'their cabinet'}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
